export const DEFAULT_ADMIN_ORG_ID = "org_test";

export const ACCESS_STORAGE_KEYS = {
  adminEmail: "glimbr-voice-admin-email",
  orgId: "glimbr-voice-org-id",
  sessionKey: "glimbr-voice-admin-session-key",
  session: "glimbr-voice-admin-session"
} as const;

export interface AccessStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AuthUser {
  id: string;
  email: string;
  emailConfirmed: boolean;
  displayName: string | null;
}

export interface AuthMembership {
  orgId: string;
  orgName: string;
  role: "owner" | "admin" | "member";
}

export interface AdminAuthSession {
  sessionKey: string;
  user: AuthUser;
  activeOrg: AuthMembership;
  memberships: AuthMembership[];
  expiresAt: number;
}

export interface AuthCredentials {
  email: string;
  password: string;
  orgName?: string;
  displayName?: string;
}

export interface RegistrationResult {
  success: boolean;
  email: string;
  emailConfirmationRequired?: boolean;
  emailConfirmed?: boolean;
  emailSendStatus?: "mocked" | "sent" | "failed";
  emailSendReason?: string;
  confirmationUrl?: string;
  runtimeOrgId?: string;
  runtimeProvisioned?: boolean;
  runtimeProvisionReason?: string;
}

export function normalizeAdminEmail(value: string): string {
  return value.trim().toLowerCase();
}

export function loadAuthSession(storage = getBrowserAccessStorage()): AdminAuthSession | null {
  if (!storage) return null;

  const rawSession = storage.getItem(ACCESS_STORAGE_KEYS.session);
  if (!rawSession) return null;

  try {
    const parsed = JSON.parse(rawSession) as Partial<AdminAuthSession>;
    if (!isAuthSession(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveAuthSession(
  session: AdminAuthSession,
  storage = getBrowserAccessStorage()
): AdminAuthSession {
  if (storage) {
    storage.setItem(ACCESS_STORAGE_KEYS.session, JSON.stringify(session));
    storage.setItem(ACCESS_STORAGE_KEYS.sessionKey, session.sessionKey);
    storage.setItem(ACCESS_STORAGE_KEYS.adminEmail, session.user.email);
    storage.setItem(ACCESS_STORAGE_KEYS.orgId, session.activeOrg.orgId);
  }

  return session;
}

export function clearAuthSession(storage = getBrowserAccessStorage()): void {
  storage?.removeItem(ACCESS_STORAGE_KEYS.session);
  storage?.removeItem(ACCESS_STORAGE_KEYS.sessionKey);
}

export function getSavedAdminEmail(storage = getBrowserAccessStorage()): string {
  return normalizeAdminEmail(storage?.getItem(ACCESS_STORAGE_KEYS.adminEmail) || "");
}

export function getAuthSessionKey(storage = getBrowserAccessStorage()): string | null {
  return loadAuthSession(storage)?.sessionKey || storage?.getItem(ACCESS_STORAGE_KEYS.sessionKey) || null;
}

export function getActiveOrgId(storage = getBrowserAccessStorage()): string {
  return loadAuthSession(storage)?.activeOrg.orgId
    || storage?.getItem(ACCESS_STORAGE_KEYS.orgId)
    || DEFAULT_ADMIN_ORG_ID;
}

export function describeAccess(session: AdminAuthSession | null): string {
  if (!session) {
    return "Sign in with email and password to manage a Glimbr Voice organization.";
  }

  return `${session.user.email} has ${session.activeOrg.role} access to ${session.activeOrg.orgName}.`;
}

export function getBrowserAccessStorage(): AccessStorage | undefined {
  if (typeof window === "undefined" || !window.localStorage) {
    return undefined;
  }

  return window.localStorage;
}

function isAuthSession(value: Partial<AdminAuthSession>): value is AdminAuthSession {
  return typeof value.sessionKey === "string"
    && typeof value.expiresAt === "number"
    && Boolean(value.user)
    && typeof value.user?.id === "string"
    && typeof value.user.email === "string"
    && typeof value.user.emailConfirmed === "boolean"
    && Boolean(value.activeOrg)
    && typeof value.activeOrg?.orgId === "string"
    && Array.isArray(value.memberships);
}
